import { type RefObject, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@/hooks/use-debounce';

export const useScrollDetection = (
    ref: RefObject<HTMLElement | null>,
    opts: {
        observeResize?: boolean;
    } = {
        observeResize: false
    }
) => {
    const [isPrevVisible, setIsPrevVisible] = useState(false);
    const [isNextVisible, setIsNextVisible] = useState(true);
    const pageRef = useRef(0);

    const handleScroll = () => {
        const { current } = ref;

        if (!current) {
            return;
        }

        const { scrollWidth, clientWidth, scrollLeft } = current;
        const maxScroll = scrollWidth - clientWidth;

        if (scrollWidth <= clientWidth && !isPrevVisible && !isNextVisible) {
            setIsPrevVisible(false);
            setIsNextVisible(false);
            return;
        }

        const shouldPrevBeVisible = scrollLeft > 0;
        const shouldNextBeVisible = scrollLeft < maxScroll - 1;

        if (shouldPrevBeVisible !== isPrevVisible) {
            setIsPrevVisible(shouldPrevBeVisible);
        }
        if (shouldNextBeVisible !== isNextVisible) {
            setIsNextVisible(shouldNextBeVisible);
        }

        const calculatedPage = Math.round(scrollLeft / clientWidth);
        if (calculatedPage !== pageRef.current) {
            pageRef.current = calculatedPage;
        }
    };

    const debouncedHandleScroll = useDebounce(handleScroll, 150);

    useEffect(() => {
        if (opts.observeResize) {
            const resizeObserver = new ResizeObserver(debouncedHandleScroll);
            resizeObserver.observe(ref.current as Element);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [opts.observeResize]);

    useEffect(() => {
        const { current } = ref;

        if (!current) {
            return;
        }

        handleScroll();
        current.addEventListener('scroll', debouncedHandleScroll);

        return () => {
            current.removeEventListener('scroll', debouncedHandleScroll);
        };
    }, [debouncedHandleScroll]);

    const handleIndicatorClick = (direction: number) => {
        if (!ref.current) {
            return;
        }

        const newPage = pageRef.current + direction;

        ref.current.scroll({
            left: ref.current.clientWidth * newPage,
            behavior: 'smooth'
        });
    };

    return {
        isNextVisible,
        isPrevVisible,
        handleIndicatorClick
    };
};
